/*eslint-env browser*/
/* eslint-disable max-lines */

import CanvasStyle from "./screen_canvas_style.js"
import topLeftAndPivot from "./topleft_and_pivot.js"
import Bezier from "./bezier.js"
import autoDimensions from "./auto_dimensions.js"
import screenImageUtils from "./screen_image_utils.js"
import ScreenImage from "./screen_image.js"
import Color from "./color.js"
import canvasTo from "./canvas_to.js"
import exportMethods from "./export_methods.js"

const {
    getCropParams,
    getUsableImageAndPositionParams,
    getImageByUrl,
    normalizeTexture
} = screenImageUtils
const {hasFillStyleParams, hasStrokeStyleParams} = CanvasStyle

const degreesToRadians = Math.PI / 180


//****************************** TEXTURES — async loading and shape anchoring

const textureKeys = ["texture", "strokeTexture"]


//returns true if any texture image must still be fetched. Mutates params to
//replace string URLs / raw image-like values with {image, ...} literals,
//then triggers the fetch and re-calls the original draw method on completion.
function ensureTexturesLoaded (screen, params, redraw) {
    textureKeys.forEach(key => normalizeTextureField(params, key))

    const urlKeys = textureKeys.filter(key => isUrlTexture(params[key]))
    if (urlKeys.length === 0) {
        return false
    }

    Promise.all(urlKeys.map(key => loadTextureImage(screen, params[key])))
        .then(redraw)
    return true
}


function normalizeTextureField (params, key) {
    const tex = params[key]
    if (!tex) {
        return
    }
    const normalized = normalizeTexture(tex)
    if (normalized !== tex) {
        params[key] = normalized
    }
}


function isUrlTexture (tex) {
    return Boolean(tex) && typeof tex.image === "string"
}


function loadTextureImage (screen, tex) {
    return new Promise(resolve => {
        getImageByUrl({
            url: tex.image,
            errorHandler: screen.errorHandler,
            callback: (loadedImage) => {
                tex.image = loadedImage
                resolve()
            }
        })
    })
}


//sets a private anchor used by canvas_style's pattern transform when a
//texture has followShape: true (so the pattern origin tracks the shape).
function applyTextureFollowShape (params, anchor) {
    if (!anchor) {
        return
    }
    const {texture, strokeTexture} = params
    if (
        (texture       && texture.followShape) ||
        (strokeTexture && strokeTexture.followShape)
    ) {
        params._textureAnchor = anchor
    }
}


let ghostCanvas
let ghostContext


function ScreenCanvas (canvas) {
    if (!ghostCanvas) {
        ghostCanvas  = newCanvas()
        ghostContext = ghostCanvas.getContext("2d")
    }

    this.canvas = canvas
    this.cx     = canvas.getContext("2d")

    this.canvasStyle = new CanvasStyle(this)
}


ScreenImage.ScreenCanvas = ScreenCanvas //to enable toScreenCanvas export while avoiding circular deps.



ScreenCanvas.prototype.updatedDimensions = function () {
    this.canvasStyle.resetStyle() //FIXME
    let {width, height} = this.canvas
    return {
        width,
        height
    }
}


ScreenCanvas.prototype.clear = function (params = {}) {
    let {
        x = 0,
        y = 0,
        width  = this.canvas.width,
        height = this.canvas.height,
        color
    } = params
    x      = Math.floor(x)
    y      = Math.floor(y)
    width  = Math.ceil(width)
    height = Math.ceil(height)

    if (color) {
        fillAllWithStyle(this, {x, y, width, height}, params)
    } else {
        this.cx.clearRect(x, y, width, height)
    }
}


ScreenCanvas.prototype.resetStyle = function () {
    this.canvasStyle.resetStyle()
}



ScreenCanvas.prototype.setOpacity = function (opacity) {
    this.canvasStyle.setOpacity(opacity)
}

ScreenCanvas.prototype.getOpacity = function () {
    return this.cx.globalAlpha
}


ScreenCanvas.prototype.isInside = function ({x, y}) {
    return x >= 0 &&
           y >= 0 &&
           x < this.canvas.width &&
           y < this.canvas.height
}



const eightBit = 255

ScreenCanvas.prototype.getPixelColor = function (point) {

    if (!this.isInside(point)) {
        return null
    }

    let canvasData = this.cx.getImageData(point.x | 0, point.y | 0, 1, 1).data // eslint-disable-line no-bitwise

    return new Color({
        red255:     canvasData[0],
        green255:   canvasData[1],
        blue255:    canvasData[2],
        opacity:    canvasData[3] / eightBit
    })
}




//****************************** EXPORTS

ScreenCanvas.prototype.toCanvas = function (params = {}) {
    return getExportCanvas(this, params)
}


// how log(screenCanvas) shows in terminals (rich_value.js): a summary with
// a lazily drawn snapshot thumbnail of the current canvas content
ScreenCanvas.prototype.terminalDisplay = function () {
    const {width, height} = this.canvas
    return {
        constructorName: "ScreenCanvas",
        widthInPixels:   width,
        heightInPixels:  height,
        thumbnail:       () => canvasTo.toThumbnailCanvas(this.canvas)
    }
}


// toImageData, toDataURL, toImageBitmap, toBlob, toBase64, toImageElement,
// toPNGImage and downloadAsImage, all over toCanvas
exportMethods.attachExportMethods(ScreenCanvas.prototype, {
    downloadName:  "downloadAsImage",
    defaultName:   "screen",
    messagePrefix: "screen.export"
})


ScreenCanvas.prototype.forToxiElement = function () {
    return this.toImageElement()
}



function getExportCanvas (screenCanvas, cropParams = {}) {
    let {canvas} = screenCanvas

    let {topLeftX, topLeftY, width, height, cropped} = getCropParams(canvas, cropParams)

    if (!cropped) {
        return canvas
    }

    ghostCanvas.width  = width
    ghostCanvas.height = height
    ghostContext.drawImage(canvas, -topLeftX, -topLeftY)
    return ghostCanvas
}








//****************************** LINES AND POLYGONS


ScreenCanvas.prototype.drawRectangle = function (params) {
    if (ensureTexturesLoaded(this, params, () => this.drawRectangle(params))) {
        return
    }
    let {width, height} = params
    let {cx, canvasStyle} = this

    let topLeft = topLeftAndPivot.getTopLeftPoint(params, width, -height)
    applyTextureFollowShape(params, topLeft)

    canvasStyle.setSpecialStyles(params)

    if (hasFillStyleParams(params)) {
        canvasStyle.setFillStyle(params)
        cx.fillRect(topLeft.x, topLeft.y, width, height)
    }

    if (hasStrokeStyleParams(params)) {
        canvasStyle.setStrokeStyle(params)
        cx.strokeRect(topLeft.x, topLeft.y, width, height)
    }

    canvasStyle.resetSpecialStyles()
}




ScreenCanvas.prototype.drawLine = function (params) {
    if (ensureTexturesLoaded(this, params, () => this.drawLine(params))) {
        return
    }
    let {origin, end, vector} = params

    if (!origin) {
        throw new Error('"origin" param is missing')
    }

    let {cx, canvasStyle} = this

    if (!end) {
        if (!vector) {
            throw new Error('"end" or "vector" param is missing')
        }
        end = {
            x: origin.x + vector.x,
            y: origin.y + vector.y
        }
    }

    applyTextureFollowShape(params, origin)

    canvasStyle.setSpecialStyles(params)

    cx.beginPath()
    cx.moveTo(origin.x, origin.y)
    cx.lineTo(end.x,    end.y)

    canvasStyle.stroke(params)
    canvasStyle.resetSpecialStyles()
}







//****************************** CIRCLES AND ARCS

ScreenCanvas.prototype.drawArc = function (params) {
    if (ensureTexturesLoaded(this, params, () => this.drawArc(params))) {
        return
    }
    let {cx, canvasStyle} = this
    let {close = true} = params

    applyTextureFollowShape(params, params.center)

    canvasStyle.setSpecialStyles(params)
    cx.beginPath()
    arcPath(cx, params)
    if (close) {
        cx.closePath()
    }
    canvasStyle.strokeAndFill(params)
    canvasStyle.resetSpecialStyles()
}




ScreenCanvas.prototype.drawPie = function (params) { //eslint-disable-line complexity
    if (ensureTexturesLoaded(this, params, () => this.drawPie(params))) {
        return
    }

    let {center, radius, innerRadius, startAngle, endAngle} = params
    let {cx, canvasStyle} = this

    if (!center) {
        throw new Error('"center" param is missing')
    }

    applyTextureFollowShape(params, center)

    canvasStyle.setSpecialStyles(params)
    cx.beginPath()

    const effectiveStart = startAngle ?? 0
    const effectiveEnd = endAngle ?? 360
    if (!innerRadius && (effectiveStart !== 0 || effectiveEnd !== 360)) {
        cx.moveTo(center.x, center.y)
    }


    arcPath(cx, {center, radius, startAngle, endAngle})


    if (innerRadius) {
        let p = {
            center,
            radius:        innerRadius,
            startAngle:    endAngle,
            endAngle:      startAngle,
            antiClockWise: true
        }

        if (!p.startAngle && !p.endAngle) {
            p.endAngle = -360
            cx.moveTo(
                center.x + this.cosAngle * innerRadius,
                center.y - this.sinAngle * innerRadius
            )
        }

        arcPath(cx, p)
    }

    cx.closePath()

    canvasStyle.strokeAndFill(params)
    canvasStyle.resetSpecialStyles()
}


ScreenCanvas.prototype.drawCircle = ScreenCanvas.prototype.drawPie









//****************************** CURVES


//almost an alias of drawFreeShape, but close is true by default
//and color means strokeColor
ScreenCanvas.prototype.drawCurve = function (params) {
    if (ensureTexturesLoaded(this, params, () => this.drawCurve(params))) {
        return
    }
    let {points, close} = params
    let smooth = params.curviness ?? params.smooth ?? 0

    verifyPoints(points)

    if (points.length < 2) {
        return
    }

    if (points.length < 3) {
        close = false
    }

    let beziersParams = Bezier.autoParamsFromPoints({points, smooth, close})

    drawCurve(this, {params, beziersParams, close, colorIsFillColor: false})

}

//almost an alias of drawCurve, but close is true by default
//and color means fillColor
ScreenCanvas.prototype.drawFreeShape = function (params) {
    if (ensureTexturesLoaded(this, params, () => this.drawFreeShape(params))) {
        return
    }
    let {points, close = true} = params
    let smooth = params.curviness ?? params.smooth ?? 0

    verifyPoints(points)

    if (points.length < 2) {
        return
    }

    if (points.length < 3) {
        close = false
    }

    let beziersParams = Bezier.autoParamsFromPoints({points, smooth, close})

    drawCurve(this, {params, beziersParams, close, colorIsFillColor: true})

}

//FIXME remove soon
ScreenCanvas.prototype.drawPolygon = ScreenCanvas.prototype.drawFreeShape


function verifyPoints (points) {
    if (!points) {
        throw new Error('"points" param is missing')
    }

    if (!Array.isArray(points)) {
        throw new Error('"points" must be an array')
    }
}


ScreenCanvas.prototype.drawLines = ScreenCanvas.prototype.drawCurve



ScreenCanvas.prototype.drawBezier = function (params) {
    if (ensureTexturesLoaded(this, params, () => this.drawBezier(params))) {
        return
    }
    drawCurve(this, {params, beziersParams: [params], colorIsFillColor: false})
}


ScreenCanvas.prototype.drawBezierPath = function (params) {
    if (ensureTexturesLoaded(this, params, () => this.drawBezierPath(params))) {
        return
    }
    drawCurve(this, {params, beziersParams: params.beziers, colorIsFillColor: false})
}






//****************************** TEXT

/* params :
*    - text
*    - bottomLeft, center, topCenter, topRight...
*    - font : "20px georgia" ,  "italic 15px arial".
*    - color or fillColor
*    - strokeColor
*    - width, height
*    - orientation
*/
ScreenCanvas.prototype.drawText = function (userParams) { //eslint-disable-line complexity
    if (ensureTexturesLoaded(this, userParams, () => this.drawText(userParams))) {
        return
    }

    let {text, orientation, flip} = userParams
    let {canvasStyle} = this

    canvasStyle.setFont(userParams)
    canvasStyle.setSpecialStyles(userParams)

    if (typeof text === "object" && !Array.isArray(text)) {
        text = JSON.stringify(text)
    }

    let params = originAndAlignmentForText(userParams)

    if (!params || !params.origin) {
        throw new Error('a position param is missing. Include one, for example "center", "topLeft"...')
    }

    params.text       = text
    params.userParams = userParams

    modifyAlignmentForFlip(flip, params)

    if (orientation || flip) {
        let screenCanvas = this
        let textOrigin = params.origin
        inOtherCoordinateSystem(this, {
            origin: textOrigin,
            orientation,
            flip,
            execute: function () {
                params.origin = {x: 0, y: 0}
                params.userParams = transformTextParamsToLocal(userParams, textOrigin, orientation)
                strokeAndFillText(screenCanvas, params)
            }
        })
    } else {
        applyTextureFollowShape(userParams, params.origin)
        strokeAndFillText(this, params)
    }
    canvasStyle.resetSpecialStyles()
}



function originAndAlignmentForText ({// eslint-disable-line complexity
    topLeft, topRight, bottomLeft, bottomRight,
    center,
    bottom, bottomCenter = bottom,
    top,    topCenter    = top,
    right,  centerRight  = right,
    left,   centerLeft   = left
}) {
    if (topLeft) {
        return {
            origin:         topLeft,
            textAlign:      "left",
            textBaseline:   "top",
            multiLineDelta: 0
        }
    } else if (topRight) {
        return {
            origin:         topRight,
            textAlign:      "right",
            textBaseline:   "top",
            multiLineDelta: 0
        }
    } else if (bottomLeft) {
        return {
            origin:         bottomLeft,
            textAlign:      "left",
            textBaseline:   "alphabetic",
            multiLineDelta: -1
        }
    } else if (bottomRight) {
        return {
            origin:         bottomRight,
            textAlign:      "right",
            textBaseline:   "alphabetic",
            multiLineDelta: -1
        }
    } else if (center) {
        return {
            origin:         center,
            textAlign:      "center",
            textBaseline:   "middle",
            multiLineDelta: -0.5
        }
    } else if (bottomCenter) {
        return {
            origin:         bottomCenter,
            textAlign:      "center",
            textBaseline:   "alphabetic",
            multiLineDelta: -1
        }
    } else if (topCenter) {
        return {
            origin:         topCenter,
            textAlign:      "center",
            textBaseline:   "top",
            multiLineDelta: 0
        }
    } else if (centerRight) {
        return {
            origin:         centerRight,
            textAlign:      "right",
            textBaseline:   "middle",
            multiLineDelta: -0.5
        }
    } else if (centerLeft) {
        return {
            origin:         centerLeft,
            textAlign:      "left",
            textBaseline:   "middle",
            multiLineDelta: -0.5
        }
    }
    return false
}


function modifyAlignmentForFlip (flip, params) { //eslint-disable-line complexity
    if (flip === "horizontal") {
        if (params.textAlign === "right") {
            params.textAlign = "left"
        } else if (params.textAlign === "left") {
            params.textAlign = "right"
        }
    } else if (flip === "vertical") {
        if (params.textBaseline === "top") {
            params.textBaseline = "alphabetic"
        } else if (params.textBaseline === "alphabetic") {
            params.textBaseline = "top"
        }
    }
}







//****************************** IMAGE


ScreenCanvas.prototype.drawImage = function (params) { //eslint-disable-line complexity
    let {image} = params

    if (typeof image === "string") {
        const {errorHandler} = this
        getImageByUrl({
            url: image,
            errorHandler,
            callback: (loadedImage) => drawImage(this, params, loadedImage)
        })
    } else {
        drawImage(this, params)
    }
}






//only here, not in screen. In screen use drawImage instead
ScreenCanvas.prototype.drawImageData = function (params) {

    let {imageData, opacity = 1} = params
    let {cx, canvasStyle} = this

    let {width, height, sourceWidth, sourceHeight, pixelSize} = imageDataDimensions(imageData, params)

    let topLeft = topLeftAndPivot.getTopLeftPoint(params, width, -height) || {x: 0, y: 0}

    let noRescaling = (width === sourceWidth && height === sourceHeight)


    if (noRescaling && opacity === 1) {
        cx.putImageData(imageData, topLeft.x, topLeft.y)
    } else {

        canvasStyle.setSpecialStyles(params)

        this.canvasStyle.setSmoothing(screenImageUtils.resolveSmoothing(params))

        prepareUnscaledGhostCanvas(this, {
            pixelSize,
            width:  sourceWidth,
            height: sourceHeight
        })

        this.ghostUnscaledCx.putImageData(imageData, 0, 0)
        cx.drawImage(
            this.ghostUnscaledCanvas,
            topLeft.x, topLeft.y,
            width, height
        )

    }

    canvasStyle.resetSpecialStyles()
}




//****************************** IMAGE TOOLS

const positionParamsConfig = {forceTopLeft: true, reverseY: true}


function drawImage (screen, params, imageFromUrl) { // eslint-disable-line complexity

    let imageCandidate = imageFromUrl || params.image

    const {image, positionParams} = getUsableImageAndPositionParams(imageCandidate, params, positionParamsConfig)


    const {scale, modifier, modifierPixelBox} = params

    let {cx, canvasStyle} = screen


    canvasStyle.setSmoothing(screenImageUtils.resolveSmoothing(params))

    if (modifier) {
        // set a ghost canvas and draw the scaled / rotated image at (0, 0)
        cx = ghostContext
    }

    canvasStyle.setSpecialStyles(params)

    if (positionParams.orientation) {
        drawRotatedImage(screen, {image, params, positionParams})
    } else if (scale) {
        // Scale param is not useful for screen but for screen canvas standalone
        throw new Error("Do you really need a scale param ? Talk to Pierre L")
    } else {
        drawStraightImage(screen, {image, positionParams})
    }

    canvasStyle.resetSpecialStyles()

    if (modifier) {
        modifier(ghostCanvas)
        getImageFromCanvas(ghostCanvas, function (newImage) {
            cx.drawImage(newImage, modifierPixelBox.x, modifierPixelBox.y)
        })
    }
}





function getImageData (image) {
    return image.imageData || (image.toImageData && image.toImageData()) //toImageData is for screen or screen_canvas
}


function drawStraightImage (screen, {image, positionParams}) { //eslint-disable-line complexity
    let {cx} = screen

    let {
        topLeft,
        width, height,
        cropSource,
        flip
    } = positionParams

    if (!topLeft) {
        throw new Error("Cannot display the image : position is missing")
    }

    let topLeftX = topLeft.x
    let topLeftY = topLeft.y

    const imageData = getImageData(image)


    if (flip === "horizontal") {
        cx.save()
        cx.translate(width + 2 * topLeftX, 0)
        cx.scale(-1, 1)
    } else if (flip === "vertical") {
        cx.save()
        cx.translate(0, height + 2 * topLeftY)
        cx.scale(1, -1)
    }


    if (imageData) {

        /* this fast method is abandonned because it replaces pixels instead of drawing over. Bad for transparency.

        let noRescaling = (width === cropSource.width && height === cropSource.height)

        if (noRescaling && opacity === 1) {
            cx.putImageData(imageData, topLeftX, topLeftY)
        } else {
        */

        prepareUnscaledGhostCanvas(screen, {
            pixelSize: 1,
            width:  cropSource.width,
            height: cropSource.height
        })

        screen.ghostUnscaledCx.putImageData(imageData, -cropSource.topLeftX, -cropSource.topLeftY)

        cx.drawImage(
            screen.ghostUnscaledCanvas,
            topLeftX, topLeftY,
            Math.ceil(width), Math.ceil(height)
        )
    } else {
        cx.drawImage(
            image,
            cropSource.topLeftX, cropSource.topLeftY,
            cropSource.width,    cropSource.height,
            topLeftX,            topLeftY,
            Math.ceil(width),    Math.ceil(height)
        )
    }

    if (flip === "horizontal" || flip === "vertical") {
        cx.restore()
    }
}



function drawRotatedImage (screen, {image, positionParams, params}) {

    let {cx} = screen

    let {
        topLeft,
        width, height,
        orientation,
        flip,
        cropSource
    } = positionParams

    if (!topLeft) {
        throw new Error("Cannot display the image : position is missing")
    }

    // the minus sign on -height and -orientation on is because y axis is towards bottom in Canvas
    let pivot = topLeftAndPivot.getPivot(params, width, -height)
    if (pivot) {
        topLeft = topLeftAndPivot.topLeftPointWithPivot(topLeft, -orientation, pivot)
    }


    function draw () {
        const imageData = getImageData(image)
        if (imageData) {
            prepareUnscaledGhostCanvas(screen, {
                pixelSize: 1,
                width:  cropSource.width,
                height: cropSource.height
            })
            screen.ghostUnscaledCx.putImageData(imageData, -cropSource.topLeftX, -cropSource.topLeftY)
            cx.drawImage(
                screen.ghostUnscaledCanvas,
                0,     0,
                width, height
            )
        } else {
            cx.drawImage(
                image,
                cropSource.topLeftX, cropSource.topLeftY,
                cropSource.width,    cropSource.height,
                0,                   0,
                width,               height
            )
        }
    }

    inOtherCoordinateSystem(screen, {
        origin: topLeft,
        orientation,
        execute: draw,
        flip,
        width,
        height
    })
}


// FIXME : the same is in ImageModifier. DRY
function getImageFromCanvas (canvas, callback) {
    let image = new Image()
    image.crossOrigin = "Anonymous"
    image.src = canvas.toDataURL()
    if (image.complete) {
        callback(image)
    } else {
        image.addEventListener("load", function () {
            callback(image)
        }, false)
    }
}


function imageDataDimensions (imageData, {width, height, pixelSize = false}) {

    let {width: sourceWidth, height: sourceHeight} = imageData

    if (pixelSize) {
        width  = sourceWidth  * pixelSize
        height = sourceHeight * pixelSize
    } else {
        pixelSize = 1
        let newDimensions = autoDimensions({width, height, sourceWidth, sourceHeight})
        width  = newDimensions.width
        height = newDimensions.height
    }
    return {width, height, sourceWidth, sourceHeight, pixelSize}
}





function prepareUnscaledGhostCanvas (screenCanvas, {width, height}) {
    if (!screenCanvas.ghostUnscaledCanvas) {
        screenCanvas.ghostUnscaledCanvas = newCanvas()
        screenCanvas.ghostUnscaledCx     = screenCanvas.ghostUnscaledCanvas.getContext("2d")
    }
    screenCanvas.ghostUnscaledCanvas.width  = width
    screenCanvas.ghostUnscaledCanvas.height = height
}





//****************************** CLEAR TOOLS

function fillAllWithStyle (screenCanvas, {x, y, width, height}, params) {
    let {cx, canvasStyle} = screenCanvas
    canvasStyle.setSpecialStyles(params)
    canvasStyle.setFillStyle(params)
    if (needsClearBeforeFill(params)) {
        cx.clearRect(x, y, width, height)
    }
    screenCanvas.cx.fillRect(x, y, width, height)
    canvasStyle.resetSpecialStyles()
}


// clear() must REPLACE pixels, not composite: a semi-transparent fill drawn source-over an
// opaque background would stay opaque. So we clear the region first whenever the fill is not a
// provably opaque color. Opaque hex/rgb/named strings keep the single-fillRect fast path.
function needsClearBeforeFill (params) {
    if ("opacity" in params) {
        return true
    }
    const {fillColor, color = fillColor} = params
    if (typeof color !== "string") {
        return true // color object, gradient or texture — clear to stay safe
    }
    try {
        return Color.convertCSSToRGB(color).opacity < 1
    } catch (e) {
        return true // a format we can't introspect (eg hsla) — clear to stay safe
    }
}

















//****************************** PATHS TOOLS



function arcPath (cx, {center, radius, startAngle = 0, endAngle = 360, antiClockWise}) {
    [startAngle, endAngle] = [-endAngle, -startAngle]

    if (radius < 0) {
        radius = 0
    }

    cx.arc(
        center.x,
        center.y,
        radius,
        startAngle * degreesToRadians,
        endAngle   * degreesToRadians,
        antiClockWise
    )
}










//****************************** TEXT TOOLS


function fillText (screenCanvas, {text, origin, userParams}) {
    if (hasFillStyleParams(userParams) || !hasStrokeStyleParams(userParams)) {
        let {cx, canvasStyle} = screenCanvas
        canvasStyle.setFillStyle(userParams)
        cx.fillText(text, origin.x, origin.y)
    }
}


function strokeText (screenCanvas, {text, origin, userParams}) {
    if (hasStrokeStyleParams(userParams) && userParams.lineWidth !== 0) {
        let {cx, canvasStyle} = screenCanvas
        canvasStyle.setStrokeStyle(userParams)
        cx.strokeText(text, origin.x, origin.y)
    }
}


const pixelSizeRegex = /[0-9.]+(?=px)/

const defaultLineHeight = 1.2

const alignToDelta = {
    left:   0,
    center: 0.5,
    right:  1
}

function strokeAndFillText (screenCanvas, { //eslint-disable-line complexity
    text,
    textAlign,
    textBaseline,
    origin,
    multiLineDelta,
    userParams
}) {
    const {cx} = screenCanvas
    cx.textBaseline = textBaseline
    let currentFont

    if (typeof text === "string") {
        let splitted = text.split("\n")
        if (splitted.length > 1) {
            text = splitted
        }
    }

    let {align, lineHeight = defaultLineHeight} = userParams

    let {font} = userParams


    let maxWidth

    if ("width" in userParams || "height" in userParams) {
        if ("height" in userParams && !userParams.useFontMetrics) {
            cx.textBaseline = "alphabetic"
        }
        currentFont = cx.font
        font = currentFont.replace(pixelSizeRegex, function (size) {
            size = parseFloat(size)
            let measure = measureForText(cx, text)
            const {factor, width} = fontScaleResult({
                measure,
                pixLineHeight: size * lineHeight,
                origin,
                userParams,
                textBaseline
            })
            maxWidth = width
            return `${size * factor}`
        })
        cx.font = font
    }


    if (align && Array.isArray(text) && align !== textAlign) {
        if (!maxWidth) {
            maxWidth = textWidth(measureForText(cx, text))
        }
        origin.x += (alignToDelta[align] - alignToDelta[textAlign]) * maxWidth
        textAlign = align
    }

    cx.textAlign = textAlign

    if (Array.isArray(text)) {
        lineHeight *= parseFloat(font.match(pixelSizeRegex)[0])
        let yStart = origin.y + multiLineDelta * lineHeight * (text.length - 1)
        text.forEach((line, i) => {
            let params = {
                text: line,
                origin: {
                    x: origin.x,
                    y: yStart + lineHeight * i
                },
                userParams
            }
            fillText(screenCanvas,   params)
            strokeText(screenCanvas, params)
        })
    } else {
        let params = {text, origin, userParams}
        fillText(screenCanvas,   params)
        strokeText(screenCanvas, params)
    }

    if (currentFont) {
        cx.font = currentFont
    }
}


ScreenCanvas.prototype.getTextDimensions = function (userParams) {
    const {cx, canvasStyle} = this
    let {text, lineHeight = defaultLineHeight} = userParams
    const currentFont = cx.font
    canvasStyle.setFont(userParams)
    let {font}  = cx

    if (typeof text === "string") {
        let splitted = text.split("\n")
        if (splitted.length > 1) {
            text = splitted
        }
    }

    let measure = measureForText(cx, text)

    let factor  = 1
    let width   = textWidth(measure)
    let height

    if ("width" in userParams || "height" in userParams) {

        font = font.replace(pixelSizeRegex, function (size) {
            size = parseFloat(size)
            let result = fontScaleResult({
                measure,
                pixLineHeight: size * lineHeight,
                userParams
            })
            factor = result.factor
            width  = result.width
            height = result.height
            return `${Math.round(10 * size * factor) / 10}`
        })
    }

    canvasStyle.setFont({font: currentFont})

    if (!height) {
        height = textHeight({
            measure,
            pixLineHeight:  parseFloat(font.match(pixelSizeRegex)[0]) * lineHeight,
            useFontMetrics: userParams.useFontMetrics
        })
    }

    return {
        width,
        font,
        height: Math.round(height)
    }
}



function fontScaleResult ({measure, pixLineHeight, origin, userParams, textBaseline}) { //eslint-disable-line complexity
    let factor

    const {useFontMetrics} = userParams

    let width  = textWidth(measure)
    let height = textHeight({measure, pixLineHeight, useFontMetrics})

    if ("width" in userParams) {
        factor = userParams.width / width
    } else {
        factor = Infinity
    }

    if ("height" in userParams) {
        factor = Math.min(factor, userParams.height / height)

        if (!useFontMetrics && origin) {
            moveTextOriginY({measure, origin, factor, textBaseline, height})
        }
    }

    return {
        factor,
        height: height * factor,
        width:  width  * factor
    }
}


function moveTextOriginY ({measure, origin, factor, textBaseline, height}) {
    if (Array.isArray(measure)) {
        if (textBaseline === "top") {
            origin.y += textHeight({measure: measure[0], useFontMetrics: false}) * factor
        } else if (textBaseline === "middle") {
            origin.y += (
                measure[0].actualBoundingBoxAscent -
                measure[measure.length - 1].actualBoundingBoxDescent
            ) * factor / 2
        } else {
            origin.y -= measure[measure.length - 1].actualBoundingBoxDescent * factor
        }
    } else {
        origin.y -= measure.actualBoundingBoxDescent * factor
        if (textBaseline === "top") {
            origin.y += height * factor
        } else if (textBaseline === "middle") {
            origin.y += height * factor / 2
        }
    }
}

function measureForText (cx, text) {
    if (Array.isArray(text)) {
        return text.map(line => cx.measureText(line))
    } else {
        return cx.measureText(text)
    }
}

function textWidth (measure) {
    if (Array.isArray(measure)) {
        return Math.max(...measure.map(m => m.width))
    } else {
        return measure.width
    }
}

function textHeight ({measure, pixLineHeight, useFontMetrics}) {
    if (Array.isArray(measure)) {
        if (useFontMetrics) {
            return measure[0].fontBoundingBoxAscent +
                measure[measure.length - 1].fontBoundingBoxDescent +
                pixLineHeight * (measure.length - 1)
        } else {
            return measure[0].actualBoundingBoxAscent +
                measure[measure.length - 1].actualBoundingBoxDescent +
                pixLineHeight * (measure.length - 1)
        }
    } else if (useFontMetrics) {
        return measure.fontBoundingBoxAscent + measure.fontBoundingBoxDescent
    } else {
        return measure.actualBoundingBoxAscent + measure.actualBoundingBoxDescent
    }

}






//****************************** CURVE TOOLS





function drawCurve (screenCanvas, {params, beziersParams, close = params.close, colorIsFillColor}) {
    let {cx, canvasStyle} = screenCanvas

    let {origin} = beziersParams[0]
    applyTextureFollowShape(params, origin)

    canvasStyle.setSpecialStyles(params)

    cx.beginPath()
    cx.moveTo(origin.x, origin.y)
    beziersParams.forEach(param => {
        if (param.controlOrigin || param.controlEnd) {
            bezierCurveTo(cx, param)
        } else {
            cx.lineTo(param.end.x, param.end.y)
        }
    })

    if (close) {
        cx.closePath()
    }

    if (colorIsFillColor || params.fillColor || params.texture) {
        canvasStyle.strokeAndFill(params)
    } else {
        canvasStyle.stroke(params)
    }

    canvasStyle.resetSpecialStyles()
}


function bezierCurveTo (cx, {origin, end, controlOrigin = origin, controlEnd = end}) {
    cx.bezierCurveTo(
        controlOrigin.x, controlOrigin.y,
        controlEnd.x,    controlEnd.y,
        end.x,           end.y
    )
}









//****************************** CONVERSIONS


function transformGradientToLocal (userParams, textOrigin, orientation) {
    let result = Object.assign({}, userParams)
    const rad = (orientation || 0) * degreesToRadians
    const cos = Math.cos(rad)
    const sin = Math.sin(rad)

    function toLocal (point) {
        let tx = point.x - textOrigin.x
        let ty = point.y - textOrigin.y
        return {
            x: tx * cos - ty * sin,
            y: tx * sin + ty * cos
        }
    }

    if (userParams.linearGradient) {
        let lg = userParams.linearGradient
        result.linearGradient = Object.assign({}, lg, {
            origin: toLocal(lg.origin),
            end:    toLocal(lg.end)
        })
    }

    if (userParams.radialGradient) {
        result.radialGradient = transformRadialGradientToLocal(userParams.radialGradient, toLocal)
    }

    return result
}


function transformRadialGradientToLocal (rg, toLocal) {
    let result = Object.assign({}, rg)
    if (rg.center) {
        result.center = toLocal(rg.center)
    }
    if (rg.innerCircle) {
        result.innerCircle = Object.assign({}, rg.innerCircle, toLocal(rg.innerCircle))
    }
    if (rg.outerCircle) {
        result.outerCircle = Object.assign({}, rg.outerCircle, toLocal(rg.outerCircle))
    }
    return result
}


function transformTextParamsToLocal (userParams, textOrigin, orientation) {
    let result = userParams
    if (userParams.linearGradient || userParams.radialGradient) {
        result = transformGradientToLocal(result, textOrigin, orientation)
    }
    if (userParams.texture || userParams.strokeTexture) {
        result = transformTextureToLocal(result, textOrigin, orientation)
        applyTextureFollowShape(result, {x: 0, y: 0})
    }
    return result
}


function transformTextureToLocal (userParams, textOrigin, orientation) {
    const transformFill   = shouldTransformTexture(userParams.texture)
    const transformStroke = shouldTransformTexture(userParams.strokeTexture)
    if (!transformFill && !transformStroke) {
        return userParams
    }
    const result = Object.assign({}, userParams)
    if (transformFill) {
        result.texture = transformOneTextureToLocal(userParams.texture, textOrigin, orientation)
    }
    if (transformStroke) {
        result.strokeTexture = transformOneTextureToLocal(userParams.strokeTexture, textOrigin, orientation)
    }
    return result
}


function shouldTransformTexture (tex) {
    return Boolean(tex && !tex.followShape)
}


function transformOneTextureToLocal (tex, textOrigin, orientation) {
    const rad = (orientation || 0) * degreesToRadians
    const cos = Math.cos(rad)
    const sin = Math.sin(rad)
    const origin = tex.origin || {x: 0, y: 0}
    const tx = origin.x - textOrigin.x
    const ty = origin.y - textOrigin.y
    return Object.assign({}, tex, {
        origin: {
            x: tx * cos - ty * sin,
            y: tx * sin + ty * cos
        },
        orientation: (tex.orientation || 0) - orientation
    })
}


function inOtherCoordinateSystem (screenCanvas, {origin, orientation = 0, execute, flip, width = 0, height = 0}) {
    const {cx, canvasStyle} = screenCanvas
    const {x, y} = origin
    cx.save()
    cx.translate(x, y)
    if (orientation) {
        cx.rotate(-orientation * degreesToRadians)
    }
    if (flip === "horizontal") {
        cx.translate(width, 0)
        cx.scale(-1, 1)
    } else if (flip === "vertical") {
        cx.translate(0, height)
        cx.scale(1, -1)
    }
    execute()
    cx.restore()
    canvasStyle.resetStyle()
}







function newCanvas () {
    if (typeof document === "undefined") {
        return new OffscreenCanvas(10, 10)
    } else {
        return document.createElement("canvas")
    }
}







export default ScreenCanvas


